import { isSystemAccount } from '@/shared/system-admin'
import { resetAiActionBilling } from '@/domain/ai-action-billing'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  AUTH_ACCESS_KEY,
  AUTH_OWNER_IMPERSONATION_KEY,
  AUTH_REFRESH_KEY,
  AUTH_TRUSTED_DEVICE_KEY,
  AUTH_USER_KEY,
  AUTH_VENDOR_IMPERSONATION_OWNER_SESSION_KEY,
} from '@/constants/auth-storage'
import { authApi } from '@/services/api/auth'
import type { StaffAuthResponse } from '@/services/api/auth'
import { hasAdminPanelAccess } from '@/shared/admin-panel-access'
import type { StaffUser } from '@/types/staff-user'

const ACCESS_KEY = AUTH_ACCESS_KEY
const REFRESH_KEY = AUTH_REFRESH_KEY
const USER_KEY = AUTH_USER_KEY
const TRUSTED_DEVICE_KEY = AUTH_TRUSTED_DEVICE_KEY
const OWNER_IMPERSONATION_KEY = AUTH_OWNER_IMPERSONATION_KEY
const VENDOR_IMPERSONATION_OWNER_SESSION_KEY = AUTH_VENDOR_IMPERSONATION_OWNER_SESSION_KEY

export interface OwnerImpersonationState {
  actorEmail?: string | null
  actorId?: string | null
  actorRole?: string | null
  merchantUserId?: number | null
  startedAt: string
  expiresAt?: string
}

interface StoredStaffSession {
  accessToken: string
  refreshToken: string
  user: StaffUser
  ownerImpersonation?: OwnerImpersonationState | null
}

let vendorOwnerSessionMemory: StoredStaffSession | null = null

function pickRefreshToken(data: { refreshToken?: string; longLivedToken?: string }): string {
  return data.refreshToken || data.longLivedToken || ''
}

function createAdminPanelAccessError(): Error {
  return new Error('auth.adminAccessDenied')
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<StaffUser | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  /** Full-screen lock overlay (Paulus CRM–style) after successful login / during logout */
  const isLoggingIn = ref(false)
  const isLoggingOut = ref(false)
  const ownerImpersonation = ref<OwnerImpersonationState | null>(null)
  const pendingEmail2FA = ref<{ challengeId: number } | null>(null)

  const isAuthenticated = computed(() => Boolean(token.value && user.value))

  function persist(
    accessToken: string,
    userData: StaffUser,
    refreshPlain = '',
    impersonation: OwnerImpersonationState | null = ownerImpersonation.value,
  ) {
    token.value = accessToken
    user.value = userData
    localStorage.setItem(ACCESS_KEY, accessToken)
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
    refreshToken.value = refreshPlain || null
    if (refreshPlain) {
      localStorage.setItem(REFRESH_KEY, refreshPlain)
    } else {
      localStorage.removeItem(REFRESH_KEY)
    }
    ownerImpersonation.value = impersonation
    if (impersonation) {
      localStorage.setItem(OWNER_IMPERSONATION_KEY, JSON.stringify(impersonation))
    } else {
      localStorage.removeItem(OWNER_IMPERSONATION_KEY)
    }
  }

  /** Merges an updated staff user (e.g. profile PATCH) into session and localStorage. */
  function mergeSessionUser(partial: StaffUser) {
    const current = user.value
    if (!current) return
    const next: StaffUser = {
      ...current,
      ...partial,
      role: partial.role ?? current.role,
    }
    user.value = next
    localStorage.setItem(USER_KEY, JSON.stringify(next))
  }

  function clear(options: { forgetTrustedDevice?: boolean } = {}) {
    resetAiActionBilling()
    token.value = null
    user.value = null
    refreshToken.value = null
    ownerImpersonation.value = null
    pendingEmail2FA.value = null
    vendorOwnerSessionMemory = null
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(OWNER_IMPERSONATION_KEY)
    localStorage.removeItem(VENDOR_IMPERSONATION_OWNER_SESSION_KEY)
    if (options.forgetTrustedDevice === true) {
      localStorage.removeItem(TRUSTED_DEVICE_KEY)
    }
  }

  /**
   * Restores session from localStorage. Refresh token rotation is best-effort:
   * local/LAN admin URLs may not receive the HttpOnly refresh cookie reliably.
   */
  async function initializeFromStorage() {
    const access = localStorage.getItem(ACCESS_KEY)
    const u = localStorage.getItem(USER_KEY)
    const o = localStorage.getItem(OWNER_IMPERSONATION_KEY)
    const rt = localStorage.getItem(REFRESH_KEY)

    if (!u || !access) {
      if (u && !access) clear()
      return
    }

    let parsedUser: StaffUser
    try {
      parsedUser = JSON.parse(u) as StaffUser
    } catch {
      clear()
      return
    }

    if (!hasAdminPanelAccess(parsedUser)) {
      clear()
      return
    }

    user.value = parsedUser
    token.value = access
    refreshToken.value = rt && rt.trim() ? rt : null
    if (o) {
      try {
        ownerImpersonation.value = JSON.parse(o) as OwnerImpersonationState
      } catch {
        ownerImpersonation.value = null
        localStorage.removeItem(OWNER_IMPERSONATION_KEY)
      }
    } else {
      ownerImpersonation.value = null
    }

    if (refreshToken.value) {
      try {
        await refreshAccessToken()
      } catch {
        if (isSystemAccount(parsedUser)) clear()
        // Normal staff may keep their access token until a real 401.
      }
    }
  }

  function persistTrustedDevice(session: { trustedDeviceToken?: string; trustedDeviceExpiresAt?: string }) {
    if (!session.trustedDeviceToken) return
    localStorage.setItem(TRUSTED_DEVICE_KEY, JSON.stringify({
      token: session.trustedDeviceToken,
      expiresAt: session.trustedDeviceExpiresAt || null,
    }))
  }

  function readTrustedDeviceToken(): string | null {
    const raw = localStorage.getItem(TRUSTED_DEVICE_KEY)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as { token?: string; expiresAt?: string | null }
      if (parsed.expiresAt && Date.parse(parsed.expiresAt) <= Date.now()) {
        localStorage.removeItem(TRUSTED_DEVICE_KEY)
        return null
      }
      return typeof parsed.token === 'string' && parsed.token.trim() ? parsed.token : null
    } catch {
      localStorage.removeItem(TRUSTED_DEVICE_KEY)
      return null
    }
  }

  function clearTrustedDevice(): void {
    localStorage.removeItem(TRUSTED_DEVICE_KEY)
  }

  async function login(email: string, password: string) {
    isLoading.value = true
    try {
      const res = await authApi.loginStaff({ email, password, trustedDeviceToken: readTrustedDeviceToken() })
      if ('requiresTwoFactor' in res && res.requiresTwoFactor === true) {
        pendingEmail2FA.value = { challengeId: res.challengeId }
        return 'needs_email_2fa' as const
      }
      const session = res as StaffAuthResponse
      pendingEmail2FA.value = null
      persistTrustedDevice(session)
      await acceptPasswordResetSession(session)
      return 'session' as const
    } finally {
      isLoading.value = false
      isLoggingIn.value = false
    }
  }

  async function verifyEmail2FA(code: string, rememberDevice = false): Promise<void> {
    const pending = pendingEmail2FA.value
    if (!pending) {
      throw new Error('No pending email 2FA challenge')
    }
    isLoading.value = true
    try {
      const res = await authApi.verifyEmailTwoFactor(pending.challengeId, code.trim(), rememberDevice)
      pendingEmail2FA.value = null
      persistTrustedDevice(res)
      await acceptPasswordResetSession(res)
    } finally {
      isLoading.value = false
      isLoggingIn.value = false
    }
  }

  async function acceptPasskeySession(session: StaffAuthResponse): Promise<void> {
    pendingEmail2FA.value = null
    await acceptPasswordResetSession(session)
  }

  function cancelEmail2FA(): void {
    pendingEmail2FA.value = null
  }

  async function refreshAccessToken() {
    const data = await authApi.refreshAccessToken(refreshToken.value || undefined)
    const nextRt = pickRefreshToken(data)
    token.value = data.accessToken
    localStorage.setItem(ACCESS_KEY, data.accessToken)
    refreshToken.value = nextRt || null
    if (nextRt) {
      localStorage.setItem(REFRESH_KEY, nextRt)
    } else {
      localStorage.removeItem(REFRESH_KEY)
    }
    if (data.user) {
      if (!hasAdminPanelAccess(data.user)) {
        clear()
        throw createAdminPanelAccessError()
      }
      user.value = data.user
      localStorage.setItem(USER_KEY, JSON.stringify(data.user))
    }
  }

  async function acceptOwnerImpersonationSession(session: {
    accessToken: string
    refreshToken: string
    user: StaffUser
    ownerImpersonation?: OwnerImpersonationState | null
  }) {
    if (!hasAdminPanelAccess(session.user)) {
      clear()
      throw createAdminPanelAccessError()
    }
    persist(
      session.accessToken,
      session.user,
      session.refreshToken,
      session.ownerImpersonation ?? {
        startedAt: new Date().toISOString(),
      },
    )
    await refreshAccessToken()
  }

  async function acceptVendorImpersonationSession(session: {
    accessToken: string
    refreshToken: string
    user: StaffUser
  }) {
    isLoggingIn.value = true
    try {
      if (!hasAdminPanelAccess(session.user)) {
        clear()
        throw createAdminPanelAccessError()
      }

      if (!user.value?.vendorImpersonation && token.value && refreshToken.value && user.value) {
        vendorOwnerSessionMemory = {
          accessToken: token.value,
          refreshToken: refreshToken.value,
          user: user.value,
          ownerImpersonation: ownerImpersonation.value,
        }
      }

      persist(session.accessToken, session.user, session.refreshToken, ownerImpersonation.value)
      await new Promise((r) => setTimeout(r, 1_400))
    } finally {
      isLoggingIn.value = false
    }
  }

  async function exitVendorImpersonation() {
    isLoggingOut.value = true
    try {
      if (!vendorOwnerSessionMemory) {
        clear()
        throw new Error('No owner session stored for vendor mode')
      }

      const ownerSession = vendorOwnerSessionMemory
      vendorOwnerSessionMemory = null
      persist(
        ownerSession.accessToken,
        ownerSession.user,
        ownerSession.refreshToken,
        ownerSession.ownerImpersonation ?? null,
      )

      await refreshAccessToken()
      await new Promise((r) => setTimeout(r, 1_400))
    } catch (error) {
      clear()
      throw error
    } finally {
      isLoggingOut.value = false
    }
  }

  async function acceptPasswordResetSession(session: {
    accessToken?: string
    authentication?: { accessToken?: string }
    refreshToken?: string
    longLivedToken?: string
    user: StaffUser
  }) {
    isLoggingIn.value = true
    try {
      if (!hasAdminPanelAccess(session.user)) {
        clear()
        throw createAdminPanelAccessError()
      }
      const access = session.accessToken || session.authentication?.accessToken
      if (!access) throw new Error('No access token in response')
      const rt = pickRefreshToken(session)
      persist(access, session.user, rt, null)
      await new Promise((r) => setTimeout(r, 1_400))
    } finally {
      isLoggingIn.value = false
    }
  }

  async function logout() {
    isLoggingOut.value = true
    const rt = refreshToken.value
    try {
      if (token.value) {
        await authApi.logoutStaff(rt ?? undefined).catch(() => {})
      }
      await new Promise((r) => setTimeout(r, 1_400))
    } catch {
      // Always clear local session
    } finally {
      clear()
      isLoggingOut.value = false
    }
  }

  return {
    user,
    token,
    refreshToken,
    isLoading,
    isLoggingIn,
    isLoggingOut,
    ownerImpersonation,
    pendingEmail2FA,
    isAuthenticated,
    initializeFromStorage,
    login,
    acceptPasskeySession,
    verifyEmail2FA,
    cancelEmail2FA,
    clearTrustedDevice,
    readTrustedDeviceToken,
    logout,
    refreshAccessToken,
    mergeSessionUser,
    acceptOwnerImpersonationSession,
    acceptVendorImpersonationSession,
    exitVendorImpersonation,
    acceptPasswordResetSession,
    clear,
  }
})
